// App.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import AdminDashboard from './components/AdminDashboard';
import RequirementsGathering from './pages/RequirementsGathering';
import Header from './components/Header';
// import LoginForm from './components/LoginForm'; // No longer used
import AutoLogin from './components/AutoLogin';
import RequirementsFlow from './pages/RequirementsFlow';
import RequirementsQuestionnaire from './components/RequirementsQuestionnaire';
import VisibilityTestHarness from './components/VisibilityTestHarness';
import DialogManager from './components/DialogManager';
import PasswordResetForm from './components/PasswordResetForm';
import Dashboard from './components/Dashboard';
import Register from './pages/Register';
import ProtectedRoute from './components/ProtectedRoute';
import Auth from './pages/Auth';
import OrganizationOnboarding from './pages/OrganizationOnboarding';
import AppDemo from './pages/AppDemo';
import { clientLogger, LogLevel } from './utils/logger';
import { authenticatedFetch, fetchOrganizations } from './utils/apiUtils';
import WelcomeSlide from './components/slides/WelcomeSlide';
import { AppDemoProvider } from './context/AppDemoContext';
import AppDemoWizard from './components/AppDemoWizard';
import LondonBookFairDashboard from './components/LondonBookFairDashboard';
import SummerWelcomeDashboard from './components/SummerWelcomeDashboard';
import HomePage from './components/HomePage';
import GlassyRegister from './pages/GlassyRegister';
import ManualDomainQueue from './pages/ManualDomainQueue';
import SimpleBrandsList from './components/SimpleBrandsList';
import StandaloneBrandsExplorer from './pages/StandaloneBrandsExplorer';
import BrandsExplorerRedirect from './pages/BrandsExplorerRedirect';
import PublicBrandsExplorer from './pages/PublicBrandsExplorer';
import PublicBrandsRedirect from './pages/PublicBrandsRedirect';
import SimplePublicPage from './pages/SimplePublicPage';
import CompletelyPublicPage from './pages/CompletelyPublicPage';
import VerifyResetToken from './components/VerifyResetToken';
import VerifyEmail from './components/VerifyEmail';
import InviteAccept from './components/InviteAccept';
import MaintenancePage from './components/MaintenancePage';
import AppAssetsSlide from './components/slides/AppAssetsSlide';
import SurveyFlow from './pages/SurveyFlow';
import { auth } from './services/firebaseService'; // Import Firebase auth

// Add global function to control logging level
window.setLogLevel = function(level) {
    const levels = {
        'none': LogLevel.NONE,
        'error': LogLevel.ERROR,
        'warn': LogLevel.WARN, 
        'info': LogLevel.INFO,
        'debug': LogLevel.DEBUG
    };
    
    const logLevel = levels[level.toLowerCase()] || LogLevel.ERROR;
    clientLogger.setLogLevel(logLevel);
    console.log(`Log level set to ${level.toUpperCase()}`);
};

// Set default log level - change to LogLevel.ERROR to suppress most logs
clientLogger.setLogLevel(LogLevel.ERROR);

// Security check - Run on every render at the App component level
const ensureSecurityCheck = () => {
  // Only allow appDemoState to exist if user is logged in
  if (!localStorage.getItem('userData')) {
    // Log that we're performing this check for debugging
    console.log('APP-LEVEL SECURITY: No user data found, clearing appDemoState');
    localStorage.removeItem('appDemoState');
    return false;
  }
  return true;
};

function App() {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [organization, setOrganization] = useState(null);
    const [dialogQueue, setDialogQueue] = useState([]);
    const [pendingResponse, setPendingResponse] = useState(null);
    const pendingResponseRef = React.useRef(null);
    const hasShownWelcomeRef = React.useRef(false);
    const [questionnaireProgress, setQuestionnaireProgress] = useState(0);
    const [featureProgress, setFeatureProgress] = useState(0);
    const [teamMembers, setTeamMembers] = useState([]);
    const [loginMessage, setLoginMessage] = useState('');
    const navigate = useNavigate();
    const location = useLocation();
    const [organizationFetchInProgress, setOrganizationFetchInProgress] = useState(false);

    // Use Firebase to manage auth state
    useEffect(() => {
        console.log('[App.jsx] Setting up Firebase auth state listener...');
        const unsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {
            console.log('[App.jsx] Firebase auth state changed:', {
                hasFirebaseUser: !!firebaseUser,
                uid: firebaseUser?.uid,
                email: firebaseUser?.email
            });

            if (firebaseUser) {
                // User is signed in.
                clientLogger.info('App.jsx: Firebase user detected.', { uid: firebaseUser.uid });

                // We can trust the user object from localStorage initially if it exists,
                // or we can fetch it. For simplicity, we'll rely on the redirect from Auth.jsx
                // to have populated localStorage.
                const storedUserData = localStorage.getItem('userData');
                console.log('[App.jsx] Checking localStorage for userData:', {
                    hasStoredData: !!storedUserData,
                    dataLength: storedUserData ? storedUserData.length : 0
                });

                if (storedUserData) {
                    try {
                        const parsedUser = JSON.parse(storedUserData);
                        console.log('[App.jsx] Parsed user data:', parsedUser);
                        setUser(parsedUser);
                        console.log('[App.jsx] User state set successfully');
                    } catch (error) {
                        console.error('[App.jsx] Error parsing stored user data:', error);
                        localStorage.removeItem('userData');
                    }
                } else {
                    console.log('[App.jsx] No stored user data found, waiting for auth flow to complete...');
                }
                // If not, we might need a fetch /me endpoint that uses the firebase token.
                // For now, we assume the login flow handles this.
                
            } else {
                // User is signed out.
                console.log('[App.jsx] No Firebase user detected, clearing user state');
                clientLogger.info('App.jsx: No Firebase user detected.');
                setUser(null);
                localStorage.removeItem('userData');
            }
            console.log('[App.jsx] Setting loading to false');
            setLoading(false);
        });

        // Cleanup subscription on unmount
        return () => unsubscribe();
    }, []);

    // Define fetchOrganization outside of useEffect so it can be referenced elsewhere
    const fetchOrganization = useCallback(async () => {
        try {
            console.log('Fetching organizations for user:', user?.id);
            // Get org ID from URL if present
            const params = new URLSearchParams(window.location.search);
            const urlOrgId = params.get('organizationId') || params.get('org'); // Support both for backwards compatibility

            try {
                // Use our authenticated fetch utility to get organizations
                const orgs = await fetchOrganizations();
                console.log('Retrieved organizations:', orgs);

                if (orgs.length === 0) {
                    console.log('No organizations found for user - redirecting to organization creation');
                    
                    // Extract domain from email
                    const userEmail = user?.email || '';
                    let createOrgPath = '/onboarding/organization';
                    
                    // Only add domain parameter if email exists
                    if (userEmail) {
                        try {
                            const emailDomain = userEmail.split('@')[1];
                            if (emailDomain) {
                                createOrgPath = `/onboarding/organization?domain=${emailDomain}`;
                            }
                        } catch (err) {
                            console.warn('Error extracting domain from email:', err);
                        }
                    }
                    
                    console.log('Redirecting to organization creation:', createOrgPath);
                    
                    // Redirect to org creation page
                    navigate(createOrgPath);
                    return;
                }

                // If URL has org ID, select that org
                if (urlOrgId) {
                    const urlOrg = orgs.find(org => org.id === urlOrgId);
                    if (urlOrg) {
                        console.log('Setting organization from URL:', urlOrg);
                        setOrganization(urlOrg);
                        
                        // If user is on onboarding page but has organizations, redirect to dashboard
                        if (location.pathname.includes('/onboarding/organization')) {
                            console.log('User has organizations but is on onboarding page, redirecting to dashboard');
                            navigate('/dashboard');
                        }
                        return;
                    }
                }

                // Otherwise use most recently selected
                const sortedOrgs = orgs.sort((a, b) => {
                    const dateA = a.last_selected ? new Date(a.last_selected) : new Date(0);
                    const dateB = b.last_selected ? new Date(b.last_selected) : new Date(0);
                    return dateB - dateA;
                });

                console.log('Setting most recent organization:', sortedOrgs[0]);
                setOrganization(sortedOrgs[0]);

                // Update URL without navigation
                if (sortedOrgs[0]) {
                    const newUrl = new URL(window.location);
                    newUrl.searchParams.set('organizationId', sortedOrgs[0].id); // Change to organizationId
                    window.history.pushState({}, '', newUrl);
                }
            } catch (fetchError) {
                console.error('Error fetching organization data:', fetchError);
                
                // Check if the error indicates the user has no organizations vs a real fetch error
                if (fetchError.message && (
                    fetchError.message.includes('User data not available') ||
                    fetchError.message.includes('401') ||
                    fetchError.message.includes('403') ||
                    fetchError.message.includes('Not authenticated')
                )) {
                    console.log('Authentication or access error - redirecting to organization creation');
                    
                    // Extract domain from email for organization creation
                    const userEmail = user?.email || '';
                    let createOrgPath = '/onboarding/organization';
                    
                    if (userEmail) {
                        try {
                            const emailDomain = userEmail.split('@')[1];
                            if (emailDomain) {
                                createOrgPath = `/onboarding/organization?domain=${emailDomain}`;
                            }
                        } catch (err) {
                            console.warn('Error extracting domain from email:', err);
                        }
                    }
                    
                    navigate(createOrgPath);
                    return;
                }
                
                // For other errors (network, server issues), redirect to organization creation
                // instead of using mock data since user likely has no organizations
                console.log('Network/server error - redirecting to organization creation rather than mock');
                
                // Extract domain from email for organization creation
                const userEmail = user?.email || '';
                let createOrgPath = '/onboarding/organization';
                
                if (userEmail) {
                    try {
                        const emailDomain = userEmail.split('@')[1];
                        if (emailDomain) {
                            createOrgPath = `/onboarding/organization?domain=${emailDomain}`;
                        }
                    } catch (err) {
                        console.warn('Error extracting domain from email:', err);
                    }
                }
                
                navigate(createOrgPath);
            }
        } catch (error) {
            console.error('Error in fetchOrganization:', error);
        } finally {
            // Reset the fetch in progress flag
            setOrganizationFetchInProgress(false);
        }
    }, [user?.id, navigate]); // Use user.id instead of user object to prevent constant re-creation

    // Show welcome dialog only after the user has logged in AND has created/joined an organization
    // This ensures users complete the organization setup process before seeing the welcome dialog
    useEffect(() => {
        if (user && organization && !localStorage.getItem('hideWelcomeScreen') && !hasShownWelcomeRef.current) {
            hasShownWelcomeRef.current = true;
            setDialogQueue(prev => [...prev, {
                type: 'welcome'
            }]);
        }
    }, [user, organization]);

    // Fetch organization when user is set
    useEffect(() => {
        if (!user) return; // Exit early if no user
        
        fetchOrganization();
    }, [user, fetchOrganization]);

    // Check if we're using the mock organization and redirect to create real org
    useEffect(() => {
        if (organization && organization.id === 'mock-org-local') {
            console.log('Detected mock organization - redirecting to organization creation');
            
            // Extract domain from email for organization creation
            const userEmail = user?.email || '';
            let createOrgPath = '/onboarding/organization';
            
            if (userEmail) {
                try {
                    const emailDomain = userEmail.split('@')[1];
                    if (emailDomain) {
                        createOrgPath = `/onboarding/organization?domain=${emailDomain}`;
                    }
                } catch (err) {
                    console.warn('Error extracting domain from email:', err);
                }
            }
            
            navigate(createOrgPath);
        }
    }, [organization, user, navigate]);

    // Additional check: if user exists but no organization after a delay, force redirect
    // TEMPORARILY DISABLED to stop redirect loop - user can manually navigate
    /*
    useEffect(() => {
        if (user && !organization && !organizationFetchInProgress) {
            console.log('User exists but no organization found - setting up redirect timer');
            
            const redirectTimer = setTimeout(() => {
                console.log('No organization found after timeout - forcing redirect to organization creation');
                
                const userEmail = user?.email || '';
                let createOrgPath = '/onboarding/organization';
                
                if (userEmail) {
                    try {
                        const emailDomain = userEmail.split('@')[1];
                        if (emailDomain) {
                            createOrgPath = `/onboarding/organization?domain=${emailDomain}`;
                        }
                    } catch (err) {
                        console.warn('Error extracting domain from email:', err);
                    }
                }
                
                navigate(createOrgPath);
            }, 3000); // Wait 3 seconds before redirecting
            
            return () => clearTimeout(redirectTimer);
        }
    }, [user, organization, organizationFetchInProgress, navigate]);
    */

    // Remove the redundant URL org ID check useEffect - fetchOrganization already handles this
    // The fetchOrganization function already checks for URL params and sets the correct organization
    // This useEffect was causing infinite loops due to dependency changes

    // New effect to fetch progress and team members
    useEffect(() => {
        if (user && organization && !user.is_admin) {
            const fetchProgressAndTeam = async () => {
                try {
                    // Fetch questionnaire data
                    const [questionsResponse, answersResponse] = await Promise.all([
                        authenticatedFetch('/api/debug/questions'),
                        authenticatedFetch(`/api/debug/responses/${organization.id}`)
                    ]);

                    if (questionsResponse.ok && answersResponse.ok) {
                        const questions = await questionsResponse.json();
                        const answers = await answersResponse.json();
                        
                        // Calculate questionnaire progress
                        const totalQuestions = questions.length;
                        const answeredQuestions = answers.filter(a => a.response_value !== null && a.response_value !== undefined).length;
                        const questionnairePercent = Math.round((answeredQuestions / totalQuestions) * 100) || 0;
                        setQuestionnaireProgress(questionnairePercent);
                    }

                    // Fetch feature data using authenticatedFetch to ensure proper session handling
                    const [featuresResponse, responsesResponse, membersResponse] = await Promise.all([
                        authenticatedFetch(`/api/features/${organization.id}`),
                        authenticatedFetch(`/api/responses/${organization.id}`),
                        authenticatedFetch(`/api/organizations/${organization.id}/members`, {
                            headers: { 'Cache-Control': 'no-cache' }
                        })
                    ]);

                    if (featuresResponse.ok && responsesResponse.ok && membersResponse.ok) {
                        const features = await featuresResponse.json();
                        const responses = await responsesResponse.json();
                        const members = await membersResponse.json();
                        
                        // Calculate feature selection progress
                        const totalFeatures = features.length;
                        const userResponses = responses.filter(r => 
                            r.organization_id === organization.id &&
                            r.user_id === user.id &&
                            r.response_value !== null
                        );
                        const featurePercent = Math.round((userResponses.length / totalFeatures) * 100) || 0;
                        setFeatureProgress(featurePercent);

                        // Process team members from the members endpoint
                        const teamList = members.map(member => ({
                            id: member.user_id || member.id,
                            name: member.name || member.user_name,
                            email: member.email || member.user_email || 'No email provided',
                            last_active: member.last_active || new Date().toISOString()
                        }));

                        // Sort by most recently active
                        teamList.sort((a, b) => new Date(b.last_active) - new Date(a.last_active));

                        setTeamMembers(teamList);
                    } else {
                        console.error('Failed to fetch data:', {
                            features: featuresResponse.ok,
                            responses: responsesResponse.ok,
                            members: membersResponse.ok
                        });
                    }
                } catch (error) {
                    console.error('Error fetching progress data:', error);
                }
            };

            // Only fetch on initial load, not on every dependency change
            fetchProgressAndTeam();
            
            // Reduce polling frequency to every 5 minutes instead of every minute
            const pollInterval = setInterval(fetchProgressAndTeam, 300000); // Poll every 5 minutes
            
            return () => clearInterval(pollInterval);
        }
    }, [user?.id, organization?.id]); // Removed navigate from dependencies as it's not needed

    // Run security check on route changes
    useEffect(() => {
        // If we're on the login page or no user is found, always clear appDemoState
        if (location.pathname === '/login' || !user) {
            localStorage.removeItem('appDemoState');
            console.log(`APP ROUTER: Cleared appDemoState due to path (${location.pathname}) or no user`);
        }
    }, [location.pathname, user]);
    
    // Run security check on app mount
    useEffect(() => {
        ensureSecurityCheck();
    }, []);

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-lg">Loading...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-green-50 via-[#f0fdf4] to-green-50 relative">
            {/* Background Pattern */}
            <div className="fixed inset-0 z-0 opacity-15 pointer-events-none" 
                style={{ 
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23065f46' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")` 
                }}
            />
            <div className="relative z-1">
                <Toaster position="top-right" />
                <DialogManager
                    dialogQueue={dialogQueue}
                    setDialogQueue={setDialogQueue}
                    pendingResponse={pendingResponse}
                    setPendingResponse={setPendingResponse}
                    pendingResponseRef={pendingResponseRef}
                    user={user}
                    organization={organization}
                />
                
                {/* Conditionally render Header for all routes except /lbf, /summer-welcome, /app-wizard, and / (home) */}
                {user && 
                    !['/lbf', '/summer-welcome', '/app-wizard', '/', '/register-lbf'].includes(location.pathname) && (
                        <Header
                            user={user}
                            currentOrganization={organization}
                            onLogout={async () => {
                                try {
                                    // Make a server-side logout request
                                    await fetch('/api/auth/logout', {
                                        method: 'POST',
                                        credentials: 'include'
                                    });
                                } catch (error) {
                                    console.error('Logout failed:', error);
                                } finally {
                                    // Clear localStorage and user state regardless of server response
                                    localStorage.removeItem('userData');
                                    
                                    // Ensure appDemoState is always cleared on logout to prevent data leakage
                                    localStorage.removeItem('appDemoState');
                                    
                                    // Clear saved material palettes to prevent accumulation across user sessions
                                    localStorage.removeItem('savedMaterialPalettes');
                                    
                                    console.log('Cleared appDemoState and palettes on logout for security');
                                    
                                    setUser(null);
                                }
                            }}
                            onOrganizationChange={setOrganization}
                            questionnaireProgress={questionnaireProgress}
                            featureProgress={featureProgress}
                            teamMembers={teamMembers}
                        />
                    )
                }

                <Routes>
                    <Route path="/" element={
                        <HomePage 
                            user={user} 
                            organization={organization} 
                        />
                    } />
                    <Route path="/completely-public" element={<CompletelyPublicPage />} />
                    <Route path="/simple-public" element={<SimplePublicPage />} />
                    
                    {/* Commenting out problematic routes 
                    <Route path="/public/brands-explorer" element={<PublicBrandsExplorer />} />
                    <Route path="/public" element={<PublicBrandsRedirect />} />
                    <Route path="/brands" element={<PublicBrandsRedirect />} />
                    <Route path="/standalone/brands-explorer" element={<StandaloneBrandsExplorer />} />
                    <Route path="/brands-redirect" element={<BrandsExplorerRedirect />} />
                    */}
                    
                    {/* Glassy UI Registration Page */}
                    <Route path="/register-lbf" element={<GlassyRegister />} />

                    {/* Other public routes */}
                    <Route path="/auto-login/:token" element={<AutoLogin onLoginSuccess={setUser} />} />
                    <Route path="/reset-password/:token" element={<PasswordResetForm />} />
                    <Route path="/login" element={<Auth />} />
                    <Route path="/register" element={<Register />} />
                    
                    {/* Special routes without the header */}
                    <Route path="/lbf" element={
                        <LondonBookFairDashboard 
                            user={user} 
                            organization={organization} 
                        />
                    } />
                    
                    <Route path="/summer-welcome" element={
                        <SummerWelcomeDashboard 
                            user={user} 
                            organization={organization} 
                        />
                    } />
                    
                    <Route path="/app-wizard" element={
                        <ProtectedRoute user={user}>
                            <AppDemoProvider>
                                <AppDemoWizard 
                                    organization={organization || {name: 'Content Publisher'}} 
                                    user={user} 
                                />
                            </AppDemoProvider>
                        </ProtectedRoute>
                    } />
                    
                    <Route path="/onboarding/organization" element={
                        <ProtectedRoute user={user}>
                            <OrganizationOnboarding />
                        </ProtectedRoute>
                    } />
                    
                    <Route path="/app-demo" element={
                        <AppDemo
                            user={user}
                            organization={organization}
                        />
                    } />
                    
                    {/* Admin routes */}
                    {user?.is_admin ? (
                        <>
                            <Route path="/admin/*" element={<AdminDashboard />} />
                            <Route path="/brands-explorer" element={<SimpleBrandsList />} />
                            <Route path="/test-harness" element={<VisibilityTestHarness />} />
                            <Route path="/" element={<Navigate to="/admin" replace />} />
                            <Route path="*" element={<Navigate to="/admin" replace />} />
                        </>
                    ) : !user ? (
                        <Route path="*" element={<Auth />} />
                    ) : organization ? (
                        <>
                            <Route path="/dashboard" element={
                                <Dashboard
                                    organization={organization}
                                    teamMembers={teamMembers}
                                    questionnaireProgress={questionnaireProgress}
                                    featureProgress={featureProgress}
                                    user={user}
                                />
                            } />
                            <Route path="/app-demo" element={
                                <AppDemo
                                    user={user}
                                    organization={organization}
                                />
                            } />
                            <Route path="/requirements" element={
                                <RequirementsFlow
                                    user={user}
                                    organization={organization}
                                />
                            } />
                            <Route path="/questionnaire" element={
                                <RequirementsQuestionnaire
                                    user={user}
                                    organization={organization}
                                />
                            } />
                            <Route path="/manual-domain-queue" element={
                                <ProtectedRoute user={user}>
                                    <ManualDomainQueue />
                                </ProtectedRoute>
                            } />
                            <Route path="*" element={<Navigate to="/" replace />} />
                        </>
                    ) : (
                        <Route path="*" element={
                            <div className="p-8">
                                <h3 className="text-xl font-bold text-red-600">
                                    Organization Access Issue
                                </h3>
                                <p className="mt-2">
                                    There seems to be a database timeout issue. You may have already created an organization.
                                </p>
                                <div className="mt-4 space-y-4">
                                    <div className="flex flex-wrap gap-3">
                                        <button
                                            onClick={async () => {
                                                console.log('Manual organization fetch triggered');
                                                try {
                                                    setOrganizationFetchInProgress(true);
                                                    await fetchOrganization();
                                                } catch (error) {
                                                    console.error('Manual fetch failed:', error);
                                                } finally {
                                                    setOrganizationFetchInProgress(false);
                                                }
                                            }}
                                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                                        >
                                            Retry Organization Fetch
                                        </button>
                                        
                                        <button
                                            onClick={async () => {
                                                console.log('Attempting direct database query...');
                                                try {
                                                    const response = await fetch('/api/debug/user-orgs', {
                                                        method: 'POST',
                                                        headers: { 'Content-Type': 'application/json' },
                                                        credentials: 'include',
                                                        body: JSON.stringify({ userId: user.id })
                                                    });
                                                    
                                                    if (response.ok) {
                                                        const orgs = await response.json();
                                                        if (orgs.length > 0) {
                                                            setOrganization(orgs[0]);
                                                            navigate('/dashboard');
                                                        } else {
                                                            console.log('No organizations found');
                                                        }
                                                    }
                                                } catch (error) {
                                                    console.error('Debug endpoint failed:', error);
                                                }
                                            }}
                                            className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
                                        >
                                            Debug Database Query
                                        </button>
                                        
                                        <button
                                            onClick={() => {
                                                const userEmail = user?.email || '';
                                                let createOrgPath = '/onboarding/organization';
                                                
                                                if (userEmail) {
                                                    try {
                                                        const emailDomain = userEmail.split('@')[1];
                                                        if (emailDomain) {
                                                            createOrgPath = `/onboarding/organization?domain=${emailDomain}`;
                                                        }
                                                    } catch (err) {
                                                        console.warn('Error extracting domain from email:', err);
                                                    }
                                                }
                                                
                                                navigate(createOrgPath);
                                            }}
                                            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                                        >
                                            Create New Organization
                                        </button>
                                    </div>
                                    <p className="text-sm text-gray-600">
                                        Automatic redirects have been disabled to prevent loops. Please use the buttons above.
                                    </p>
                                </div>
                                <pre className="mt-4 p-4 bg-gray-100 rounded text-xs">
                                    User ID: {user.id}
                                    {'\n'}
                                    Email: {user.email}
                                </pre>
                            </div>
                        } />
                    )}

                    {/* Add this direct access route outside of conditional blocks */}
                    <Route path="/direct/brands-explorer" element={
                        <ProtectedRoute user={user} adminRequired={true}>
                            <SimpleBrandsList />
                        </ProtectedRoute>
                    } />

                    {/* Main brands explorer route for admin users */}
                    <Route path="/brands-explorer" element={
                        <ProtectedRoute user={user} adminRequired={true}>
                            <SimpleBrandsList />
                        </ProtectedRoute>
                    } />

                    {/* Special standalone route that handles its own auth */}
                    <Route path="/standalone/brands-explorer" element={<StandaloneBrandsExplorer />} />
                    <Route path="/brands-redirect" element={<BrandsExplorerRedirect />} />

                    {/* Public routes that don't require authentication */}
                    <Route path="/reset-password" element={<PasswordResetForm />} />
                    <Route path="/verify-reset-token/:token" element={<VerifyResetToken />} />
                    <Route path="/verify-email/:token" element={<VerifyEmail />} />
                    <Route path="/invite/:token" element={<InviteAccept />} />
                    <Route path="/maintenance" element={<MaintenancePage />} />

                    {/* New routes for survey */}
                    <Route path="/survey/:uuid" element={<SurveyFlow />} />
                    <Route path="/survey" element={<SurveyFlow />} />
                </Routes>
            </div>
        </div>
    );
}

export default App;